import { computed, defineComponent, onMounted, reactive, ref, toRefs, unref } from 'vue';
import { useRouter } from 'vue-router';
import { ElForm, ElMessage, ElMessageBox } from 'element-plus';
import { addThresholdRule, deleteThresholdRule, getThresholdAlarmOptions, getThresholdIndicatorOptions, getThresholdRulePage, updateThresholdRule, updateThresholdRuleStatus } from '@/api/thresholdRule';
const createForm = () => ({
  id: null,
  vendorCode: 1,
  deviceModel: null,
  indicatorCode: '',
  indicatorName: '',
  operator: '>',
  thresholdValue: null,
  alarmCode: '',
  hysteresis: 0,
  cooldownSec: 300,
  isEnabled: 1
});
export default defineComponent({
  name: 'ThresholdRule',
  setup() {
    const router = useRouter();
    const formContainer = ref(ElForm);
    const tableTitle = computed(() => router.currentRoute.value.meta?.title || '告警阈值规则');
    const indicatorOptions = ref([]);
    const alarmOptions = ref([]);
    const state = reactive({
      tableLoading: false,
      saveLoading: false,
      dialogVisible: false,
      title: '新增告警阈值规则',
      queryParams: {
        indicatorCode: '',
        alarmCode: '',
        isEnabled: null,
        pageNum: 1,
        pageSize: 15
      },
      tableData: {
        rows: [],
        total: 0
      },
      form: createForm(),
      searchFormData: [],
      formHandle: [],
      tableHeader: [],
      formData: [],
      formRules: {
        indicatorCode: [{
          required: true,
          message: '请选择指标',
          trigger: 'change'
        }],
        operator: [{
          required: true,
          message: '请选择比较运算符',
          trigger: 'change'
        }],
        thresholdValue: [{
          required: true,
          message: '阈值不能为空',
          trigger: 'blur'
        }],
        alarmCode: [{
          required: true,
          message: '告警编码不能为空',
          trigger: 'change'
        }]
      }
    });
    const handleIndicatorChange = code => {
      const selected = indicatorOptions.value.find(item => item.indicatorCode === code);
      state.form.indicatorName = selected?.indicatorName || '';
    };
    const methods = {
      getList() {
        state.tableLoading = true;
        getThresholdRulePage(state.queryParams).then(response => {
          state.tableData.rows = response.data?.records || [];
          state.tableData.total = response.data?.total || 0;
        }).finally(() => {
          state.tableLoading = false;
        });
      },
      handleQuery() {
        state.queryParams.pageNum = 1;
        methods.getList();
      },
      resetQuery() {
        Object.assign(state.queryParams, {
          indicatorCode: '',
          alarmCode: '',
          isEnabled: null,
          pageNum: 1,
          pageSize: 10
        });
        methods.getList();
      },
      handleAdd() {
        state.form = createForm();
        state.title = '新增告警阈值规则';
        state.dialogVisible = true;
      },
      handleUpdate(row) {
        state.form = {
          ...createForm(),
          ...row,
          vendorCode: 1,
          deviceModel: null
        };
        state.title = '修改告警阈值规则';
        state.dialogVisible = true;
      },
      resetForm() {
        state.form = createForm();
      },
      submitForm() {
        unref(formContainer).commonForm.validate(valid => {
          if (!valid) return;
          state.saveLoading = true;
          const request = state.form.id ? updateThresholdRule : addThresholdRule;
          const payload = {
            ...state.form,
            vendorCode: 1,
            deviceModel: null
          };
          request(payload).then(() => {
            ElMessage.success(state.form.id ? '修改成功' : '新增成功');
            state.dialogVisible = false;
            methods.getList();
          }).finally(() => {
            state.saveLoading = false;
          });
        });
      },
      handleDelete(row) {
        ElMessageBox.confirm(`确定删除规则“${row.indicatorName || row.indicatorCode}”吗？`, '警告', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => deleteThresholdRule(row.id)).then(() => {
          ElMessage.success('删除成功');
          methods.getList();
        }).catch(error => error);
      },
      handleStatusChange(row) {
        const status = row.isEnabled;
        updateThresholdRuleStatus(row.id, status).then(() => {
          ElMessage.success(status === 1 ? '启用成功' : '停用成功');
        }).catch(() => {
          row.isEnabled = status === 1 ? 0 : 1;
        });
      }
    };
    const buildPageConfig = () => {
      const optionProps = {
        label: 'indicatorName',
        value: 'indicatorCode'
      };
      const alarmOptionProps = {
        label: 'optionLabel',
        value: 'alarmCode'
      };
      state.searchFormData = [{
        prop: 'indicatorCode',
        label: '指标名称',
        type: 'list',
        clearable: true,
        filterable: true,
        span: 5,
        options: indicatorOptions.value,
        props: optionProps
      }, {
        prop: 'isEnabled',
        label: '启用状态',
        type: 'list',
        clearable: true,
        span: 5,
        width: '160px',
        options: [{
          label: '启用',
          value: 1
        }, {
          label: '停用',
          value: 0
        }],
        props: {
          label: 'label',
          value: 'value'
        }
      }];
      state.formData = [{
        prop: 'indicatorCode',
        label: '指标',
        type: 'list',
        filterable: true,
        span: 24,
        options: indicatorOptions.value,
        props: optionProps,
        change: handleIndicatorChange
      }, {
        prop: 'operator',
        label: '比较运算符',
        type: 'list',
        span: 12,
        options: ['>', '>=', '<', '<=', '=', '!='].map(value => ({
          label: value,
          value
        })),
        props: {
          label: 'label',
          value: 'value'
        }
      }, {
        prop: 'thresholdValue',
        label: '阈值',
        placeholder: '请输入阈值',
        type: 'textNum',
        precision: 2,
        step: 0.01,
        span: 12
      }, {
        prop: 'alarmCode',
        label: '告警编码',
        type: 'list',
        filterable: true,
        span: 24,
        options: alarmOptions.value,
        props: alarmOptionProps
      }, {
        prop: 'hysteresis',
        label: '回差值',
        placeholder: '请输入回差值',
        type: 'textNum',
        min: 0,
        span: 12
      }, {
        prop: 'cooldownSec',
        label: '冷却时间(秒)',
        placeholder: '请输入冷却时间',
        type: 'textNum',
        min: 0,
        span: 12
      }, {
        prop: 'isEnabled',
        label: '启用状态',
        type: 'radio',
        span: 12,
        options: [{
          dictLabel: '启用',
          dictValue: 1
        }, {
          dictLabel: '停用',
          dictValue: 0
        }],
        props: {
          label: 'dictLabel',
          value: 'dictValue'
        }
      }];
    };
    state.formHandle = [{
      label: '搜索',
      type: 'primary',
      icon: 'el-icon-search',
      class: 'btn-liquid',
      handle: methods.handleQuery
    }, {
      label: '重置',
      icon: 'el-icon-refresh',
      class: 'commonDarkBtn',
      handle: methods.resetQuery
    }];
    state.tableHeader = [{
      prop: 'indicatorName',
      label: '指标名称',
      type: 'text'
    }, {
      prop: 'operator',
      label: '运算符',
      type: 'text'
    }, {
      prop: 'thresholdValue',
      label: '阈值',
      type: 'text'
    }, {
      prop: 'alarmName',
      label: '告警名称',
      type: 'text'
    }, {
      prop: 'alarmLevel',
      label: '告警级别',
      statusList: [{
        value: 1,
        label: '致命'
      }, {
        value: 2,
        label: '严重'
      }, {
        value: 3,
        label: '一般'
      }, {
        value: 4,
        label: '提示'
      }]
    }, {
      prop: 'hysteresis',
      label: '回差值',
      type: 'text'
    }, {
      prop: 'cooldownSec',
      label: '冷却(秒)',
      type: 'text'
    }, {
      prop: 'isEnabled',
      label: '启用状态',
      switch: {
        active: 1,
        inActive: 0,
        changeFun: methods.handleStatusChange
      }
    }, {
      prop: 'operation',
      label: '操作',
      width: '130px',
      fixed: 'right',
      operation: [{
        name: '修改',
        clickFun: methods.handleUpdate
      }, {
        name: '删除',
        clickFun: methods.handleDelete
      }]
    }];
    onMounted(() => {
      getThresholdIndicatorOptions().then(response => {
        indicatorOptions.value = response.data || [];
        buildPageConfig();
      });
      getThresholdAlarmOptions().then(response => {
        alarmOptions.value = (response.data || []).map(item => ({
          ...item,
          optionLabel: `${item.alarmName}（${item.alarmCode}）`
        }));
        buildPageConfig();
      });
      methods.getList();
    });
    return {
      ...toRefs(state),
      ...methods,
      formContainer,
      tableTitle
    };
  }
});